import {TFunction} from 'i18next';
import {
  CLINICAL_SUPPORT__EXAMS_ID,
  CLINICAL_SUPPORT__HEALTH_APPOINTMENTS_ID,
  CLINICAL_SUPPORT__MEDICATION_ID,
  CLINICAL_SUPPORT__NURSING_ID,
  CLINICAL_SUPPORT__OTHER_ID,
  CLINICAL_SUPPORT__PHYSIOTHERAPY_ID,
  CLINICAL_SUPPORT__SURGERY_ID,
  CLINICAL_SUPPORT__VACCINATION_ID,
  CLINICAL_SUPPORT__VITAL_SIGNS_ID,
  BADL__BATH_ID,
  BADL__CLOTHING_ID,
  BADL__ELIMINATION_ID,
  BADL__FOOD_ID,
  BADL__OTHER_ID,
  BADL__PERSONAL_HYGIENE_ID,
  BADL__POSITION_MOBILITY_ID,
  BADL__SLEEP_ID,
  IADL__ESCORT_ID,
  IADL__HOUSEKEEPING_ID,
  IADL__LAUNDRY_ID,
  IADL__OTHER_ID,
  IADL__SHOPPING_ID,
  IADL__TRANSPORT_ID,
  PERSONAL_DEVELOPMENT__COGNITIVE_INTELLECTUAL_ID,
  PERSONAL_DEVELOPMENT__OTHER_ID,
  PERSONAL_DEVELOPMENT__PHYSICS_MOVEMENT_ID,
  PERSONAL_DEVELOPMENT__RECREATIONAL_ARTISTIC_ID,
  PERSONAL_DEVELOPMENT__RELIGION_SPIRITUALITY_ID,
  PERSONAL_DEVELOPMENT__DAILY_OCCUPATION_ID,
  PERSONAL_DEVELOPMENT__SENSORY_ID,
  PERSONAL_DEVELOPMENT__SOCIOCULTURAL_ID,
  PERSONAL_DEVELOPMENT__SOCIAL_INTERACTION_ID,
} from '../../../../constants/hardcoded';
import {ExportCarePlanGroupDataProcessor} from './export-care-plan-group-data-processor';
import {exportCarePlanDebugData} from './export-care-plan-debug-data';
import {exportCarePlanProcessReportData} from './export-care-plan-process-report-data';
import {
  ExportCarePlanData,
  ExportCarePlanReportData,
  ExportCarePlanTableData,
} from './export-care-plan-types';
import {MedicationXlsxDataProcessor} from '../clinical-support/medication/export-care-plan-medication-xlsx-data';
import {VitalSignsXlsxDataProcessor} from '../clinical-support/vital-signs/export-care-plan-vital-signs-xlsx-data';
import {HealthAppointmentsXlsxDataProcessor} from '../clinical-support/health-appointments/export-care-plan-health-appointments-xlsx-data';
import {NursingXlsxDataProcessor} from '../clinical-support/nursing/export-care-plan-nursing-xlsx-data';
import {VaccinationXlsxDataProcessor} from '../clinical-support/vaccination/export-care-plan-vaccination-xlsx-data';
import {ExamsXlsxDataProcessor} from '../clinical-support/exams/export-care-plan-exams-xlsx-data';
import {PhysiotherapyXlsxDataProcessor} from '../clinical-support/physiotherapy/export-care-plan-physiotherapy-xlsx-data';
import {SurgeryXlsxDataProcessor} from '../clinical-support/surgery/export-care-plan-surgery-xlsx-data';
import {OtherXlsxDataProcessor} from '../clinical-support/other/export-care-plan-other-xlsx-data';
import {CognitiveIntellectualXlsxDataProcessor} from '../personal-development/cognitive-intellectual/export-care-plan-cognitive-intellectual-xlsx-data';
import {PersDevelopOtherXlsxDataProcessor} from '../personal-development/other/export-care-plan-other-xlsx-data';
import {PhysicsMovementXlsxDataProcessor} from '../personal-development/physics-movement/export-care-plan-physics-movement-xlsx-data';
import {RecreationalArtisticXlsxDataProcessor} from '../personal-development/recreational-artistic/export-care-plan-recreational-artistic-xlsx-data';
import {SocialInteractionXlsxDataProcessor} from '../personal-development/social-interaction/export-care-plan-social-interaction-xlsx-data';
import {ReligionSpiritualityXlsxDataProcessor} from '../personal-development/religion-spirituality/export-care-plan-religion-spirituality-xlsx-data';
import {SocioculturalXlsxDataProcessor} from '../personal-development/sociocultural/export-care-plan-sociocultural-xlsx-data';
import {DailyOccupationXlsxDataProcessor} from '../personal-development/daily-occupation/export-care-plan-daily-occupation-xlsx-data';
import {SensoryXlsxDataProcessor} from '../personal-development/sensory/export-care-plan-sensory-xlsx-data';
import {TransportXlsxDataProcessor} from '../iadl/transport/export-care-plan-transport-xlsx-data';
import {LaundryXlsxDataProcessor} from '../iadl/laundry/export-care-plan-laundry-xlsx-data';
import {HousekeepingXlsxDataProcessor} from '../iadl/housekeeping/export-care-plan-housekeeping-xlsx-data';
import {EscortXlsxDataProcessor} from '../iadl/escort/export-care-plan-escort-xlsx-data';
import {ShoppingXlsxDataProcessor} from '../iadl/shopping/export-care-plan-shopping-xlsx-data';
import {AivdOtherXlsxDataProcessor} from '../iadl/other/export-care-plan-other-xlsx-data';
import {FoodXlsxDataProcessor} from '../badl/food/export-care-plan-food-xlsx-data';
import {ClothingXlsxDataProcessor} from '../badl/clothing/export-care-plan-clothing-xlsx-data';
import {PersonalHygieneXlsxDataProcessor} from '../badl/personal-hygiene/export-care-plan-personal-hygiene-xlsx-data';
import {BathXlsxDataProcessor} from '../badl/bath/export-care-plan-bath-xlsx-data';
import {SleepXlsxDataProcessor} from '../badl/sleep/export-care-plan-sleep-xlsx-data';
import {PositionMobilityXlsxDataProcessor} from '../badl/position-mobility/export-care-plan-position-mobility-xlsx-data';
import {EliminationXlsxDataProcessor} from '../badl/elimination/export-care-plan-elimination-xlsx-data';
import {BadlOtherXlsxDataProcessor} from '../badl/other/export-care-plan-other-xlsx-data';

// const appConfig: AppConfig = require('../../../../../config.json');

export async function exportCarePlanActivitiesGroupXlsxData(
  reportData: ExportCarePlanReportData,
  t: TFunction
): Promise<ExportCarePlanTableData> {
  // 1. Process report data
  const baseData = exportCarePlanProcessReportData(reportData);
  // 2. Process data for xlsx (tables)
  const tableData = await exportCarePlanActivitiesGroupXlsxProcessData(baseData, t);
  // 3. Debug
  // if (appConfig.environment === 'development')
  exportCarePlanDebugData(reportData, baseData, tableData);

  // 4. Done
  return tableData;
}

// --------------------------------------------------------------------------
// --------------------------------------------------------------------------
// --------------------------------------------------------------------------

async function exportCarePlanActivitiesGroupXlsxProcessData(
  baseData: ExportCarePlanData,
  t: TFunction
): Promise<ExportCarePlanTableData> {
  switch (baseData.group.id) {
    case BADL__FOOD_ID:
      return new FoodXlsxDataProcessor(baseData, t).process();

    case BADL__CLOTHING_ID:
      return new ClothingXlsxDataProcessor(baseData, t).process();

    case BADL__PERSONAL_HYGIENE_ID:
      return new PersonalHygieneXlsxDataProcessor(baseData, t).process();

    case BADL__BATH_ID:
      return new BathXlsxDataProcessor(baseData, t).process();

    case BADL__SLEEP_ID:
      return new SleepXlsxDataProcessor(baseData, t).process();

    case BADL__POSITION_MOBILITY_ID:
      return new PositionMobilityXlsxDataProcessor(baseData, t).process();

    case BADL__ELIMINATION_ID:
      return new EliminationXlsxDataProcessor(baseData, t).process();

    case BADL__OTHER_ID:
      return new BadlOtherXlsxDataProcessor(baseData, t).process();

    case IADL__TRANSPORT_ID:
      return new TransportXlsxDataProcessor(baseData, t).process();

    case IADL__LAUNDRY_ID:
      return new LaundryXlsxDataProcessor(baseData, t).process();

    case IADL__HOUSEKEEPING_ID:
      return new HousekeepingXlsxDataProcessor(baseData, t).process();

    case IADL__ESCORT_ID:
      return new EscortXlsxDataProcessor(baseData, t).process();

    case IADL__SHOPPING_ID:
      return new ShoppingXlsxDataProcessor(baseData, t).process();

    case IADL__OTHER_ID:
      return new AivdOtherXlsxDataProcessor(baseData, t).process();

    case CLINICAL_SUPPORT__MEDICATION_ID:
      return new MedicationXlsxDataProcessor(baseData, t).process();

    case CLINICAL_SUPPORT__VITAL_SIGNS_ID:
      return new VitalSignsXlsxDataProcessor(baseData, t).process();

    case CLINICAL_SUPPORT__HEALTH_APPOINTMENTS_ID:
      return new HealthAppointmentsXlsxDataProcessor(baseData, t).process();

    case CLINICAL_SUPPORT__NURSING_ID:
      return new NursingXlsxDataProcessor(baseData, t).process();

    case CLINICAL_SUPPORT__VACCINATION_ID:
      return new VaccinationXlsxDataProcessor(baseData, t).process();

    case CLINICAL_SUPPORT__EXAMS_ID:
      return new ExamsXlsxDataProcessor(baseData, t).process();

    case CLINICAL_SUPPORT__PHYSIOTHERAPY_ID:
      return new PhysiotherapyXlsxDataProcessor(baseData, t).process();

    case CLINICAL_SUPPORT__SURGERY_ID:
      return new SurgeryXlsxDataProcessor(baseData, t).process();

    case CLINICAL_SUPPORT__OTHER_ID:
      return new OtherXlsxDataProcessor(baseData, t).process();

    case PERSONAL_DEVELOPMENT__COGNITIVE_INTELLECTUAL_ID:
      return new CognitiveIntellectualXlsxDataProcessor(baseData, t).process();

    case PERSONAL_DEVELOPMENT__OTHER_ID:
      return new PersDevelopOtherXlsxDataProcessor(baseData, t).process();

    case PERSONAL_DEVELOPMENT__PHYSICS_MOVEMENT_ID:
      return new PhysicsMovementXlsxDataProcessor(baseData, t).process();

    case PERSONAL_DEVELOPMENT__RECREATIONAL_ARTISTIC_ID:
      return new RecreationalArtisticXlsxDataProcessor(baseData, t).process();

    case PERSONAL_DEVELOPMENT__SOCIAL_INTERACTION_ID:
      return new SocialInteractionXlsxDataProcessor(baseData, t).process();

    case PERSONAL_DEVELOPMENT__RELIGION_SPIRITUALITY_ID:
      return new ReligionSpiritualityXlsxDataProcessor(baseData, t).process();

    case PERSONAL_DEVELOPMENT__SOCIOCULTURAL_ID:
      return new SocioculturalXlsxDataProcessor(baseData, t).process();

    case PERSONAL_DEVELOPMENT__DAILY_OCCUPATION_ID:
      return new DailyOccupationXlsxDataProcessor(baseData, t).process();

    case PERSONAL_DEVELOPMENT__SENSORY_ID:
      return new SensoryXlsxDataProcessor(baseData, t).process();

    default:
      return new ExportCarePlanGroupDataProcessor(baseData, t).process();
  }
}
